/**
 * Client-side site search (products, articles, tunes/indexes, static pages).
 */

import { alternateTitlesSearchText } from './tuneAlternateTitles';

/** @typedef {'all'|'products'|'articles'|'tunes'|'pages'} SiteSearchScope */

// Common English words that carry no search signal.
const STOP_WORDS = new Set([
  'a','an','the','and','or','but','in','on','at','to','for','of','with',
  'is','are','was','were','be','been','being',
  'show','me','all','from','give','find','list','get','what','which',
  'some','any','have','do','does','did','can','could','please','about',
  'want','need','looking','search','this','that','these','those','also',
]);

/**
 * Lowercase, strip apostrophes, split punctuation into word boundaries.
 */
export function normalizeSearchText(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[''`´]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Individual words from a title (or name) for partial / prefix matching.
 */
export function titleSearchWords(title) {
  const norm = normalizeSearchText(title);
  return norm ? norm.split(' ').filter((w) => w.length > 1) : [];
}

/**
 * Split a natural-language query into meaningful search tokens.
 * Stop words and single-character tokens are removed.
 * If stripping leaves nothing, returns the original trimmed text as one token
 * so short / all-stop-word queries still work.
 */
export function tokenizeQuery(text) {
  const raw = String(text || '').trim().toLowerCase();
  if (!raw) return [];
  const tokens = raw.split(/\s+/).filter((t) => t.length > 1 && !STOP_WORDS.has(t));
  return tokens.length > 0 ? tokens : [raw];
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Word-boundary test on normalized haystack (spaces as separators). */
function hasWordToken(hayNorm, tokenNorm) {
  if (!tokenNorm) return true;
  return new RegExp(`(?:^|\\s)${escapeRegExp(tokenNorm)}(?:\\s|$)`).test(` ${hayNorm} `);
}

/**
 * Whether a single token matches the haystack or title word list.
 * Supports substring, plural stems, apostrophe-insensitive match, and title prefixes.
 * Short tokens (≤3 chars) require a whole-word hit so "ten" does not match "often".
 */
function tokenMatchesSearch(token, haystackLower, titleWords = []) {
  const tokenNorm = normalizeSearchText(token);
  if (!tokenNorm) return true;

  const hay = String(haystackLower || '').toLowerCase();
  const hayNorm = normalizeSearchText(hay);

  for (const word of titleWords) {
    if (word === tokenNorm || word.startsWith(tokenNorm)) return true;
    if (tokenNorm.length >= 3 && word.includes(tokenNorm)) return true;
  }

  // Short tokens: whole word only (avoids "ten" ⊂ "often" / "intended" / "content")
  if (tokenNorm.length <= 3) {
    return hasWordToken(hayNorm, tokenNorm);
  }

  if (hay.includes(tokenNorm) || hayNorm.includes(tokenNorm)) return true;
  if (hasWordToken(hayNorm, tokenNorm)) return true;

  if (tokenNorm.endsWith('s') && hayNorm.includes(tokenNorm.slice(0, -1))) {
    return true;
  }
  if (tokenNorm.length > 4 && tokenNorm.endsWith('es') && hayNorm.includes(tokenNorm.slice(0, -2))) {
    return true;
  }

  return false;
}

/**
 * Returns true when the haystack contains every token.
 * For tokens ending in 's' (plurals) also tries the singular form so
 * "reels" matches hay that only says "reel".
 */
export function matchesAllTokens(haystackLower, tokens, titleWords = []) {
  return tokens.every((token) => tokenMatchesSearch(token, haystackLower, titleWords));
}

/**
 * Shared client search: tokenize query (stop words, multi-word AND), optional plural stems.
 * Empty / whitespace-only query matches everything.
 * @param {string} haystackLower - lowercased haystack from *RowSearchHaystack helpers
 * @param {string} searchText - raw user input
 * @param {{ title?: string }} [options] - when set, enables title phrase + word-prefix matching
 */
export function queryMatchesSearchText(haystackLower, searchText, options = {}) {
  const raw = String(searchText ?? '').trim();
  if (!raw) return true;
  const tokens = tokenizeQuery(searchText);
  if (!tokens.length) return true;

  const titleWords = options.title ? titleSearchWords(options.title) : [];
  const titleNorm = options.title ? normalizeSearchText(options.title) : '';
  const phraseNorm = normalizeSearchText(raw);
  const tokenPhraseNorm = normalizeSearchText(tokens.join(' '));

  // Full typed phrase in title (e.g. "ten to tunes" → "It's always Ten to Tunes")
  if (titleNorm && phraseNorm.length >= 3 && titleNorm.includes(phraseNorm)) {
    return true;
  }
  // Token phrase without stop words (e.g. "more tunes")
  if (titleNorm && tokenPhraseNorm.length >= 3 && titleNorm.includes(tokenPhraseNorm)) {
    return true;
  }

  return matchesAllTokens(String(haystackLower || '').toLowerCase(), tokens, titleWords);
}

/** 1 = first volume, 2 = second, 3 = third, null = not a numbered collection title. */
export function detectCollectionOrdinal(titleNorm) {
  const t = normalizeSearchText(titleNorm);
  if (/\b(1st|first)\b/.test(t)) return 1;
  if (/\b(2nd|second)\b/.test(t)) return 2;
  if (/\b(3rd|third)\b/.test(t)) return 3;
  return null;
}

/** 1 = first volume, 2 = second, 3 = third, 99 = not numbered / other. */
export function effectiveCollectionOrdinal(productName) {
  const titleNorm = normalizeSearchText(productName);
  const detected = detectCollectionOrdinal(titleNorm);
  if (detected != null) return detected;
  if (
    /collection/.test(titleNorm) &&
    !/\b(2nd|3rd|second|third)\b/.test(titleNorm)
  ) {
    return 1;
  }
  return 99;
}

/**
 * Higher score = better match. Used to sort /shop search results by relevance.
 * @param {object} product
 * @param {string} searchText
 */
export function scoreProductSearchRelevance(product, searchText) {
  const raw = String(searchText ?? '').trim();
  if (!raw) return 0;

  const tokens = tokenizeQuery(searchText);
  const titleNorm = normalizeSearchText(product.name);
  const phraseNorm = normalizeSearchText(tokens.join(' '));
  const artistNorm = normalizeSearchText(
    [product.artist, product.author].filter(Boolean).join(' '),
  );
  const tagsNorm = normalizeSearchText(
    typeof product.tags === 'string' ? product.tags : '',
  );

  let score = 0;

  if (phraseNorm.length >= 2 && titleNorm.includes(phraseNorm)) {
    score += 120;
  }

  for (const token of tokens) {
    const t = normalizeSearchText(token);
    if (!t) continue;

    if (titleNorm.includes(t)) {
      if (titleNorm === t) score += 70;
      else if (new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(titleNorm)) score += 50;
      else score += 32;
    }

    if (artistNorm.includes(t)) {
      if (new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(artistNorm)) score += 35;
      else score += 22;
    }

    if (tagsNorm.includes(t) && !titleNorm.includes(t) && !artistNorm.includes(t)) {
      score += 10;
    } else if (tagsNorm.includes(t)) {
      score += 4;
    }
  }

  const queryNamesVolume = /\b(1st|2nd|3rd|first|second|third)\b/i.test(raw);
  const ordinal = effectiveCollectionOrdinal(product.name);

  if (!queryNamesVolume) {
    if (ordinal === 1) score += 120;
    else if (ordinal === 2) score += 75;
    else if (ordinal === 3) score += 35;
  } else if (ordinal !== 99) {
    score += 25;
  }

  const titleOrArtistHit = tokens.some((tok) => {
    const t = normalizeSearchText(tok);
    return titleNorm.includes(t) || artistNorm.includes(t);
  });
  if (!titleOrArtistHit && tokens.some((tok) => tagsNorm.includes(normalizeSearchText(tok)))) {
    score -= 30;
  }

  if (product.featured === 1 || product.featured === true) {
    score += 2;
  }

  return score;
}

/**
 * Higher score = better match. Used to rank article hits in site search and /articles browse.
 * @param {{ title?: string, description?: string, excerpt?: string, page_id?: string, id?: string, featured?: boolean|number }} article
 * @param {string} searchText
 */
export function scoreArticleSearchRelevance(article, searchText) {
  const raw = String(searchText ?? '').trim();
  if (!raw) return 0;

  const tokens = tokenizeQuery(searchText);
  const titleNorm = normalizeSearchText(article.title);
  const rawPhraseNorm = normalizeSearchText(raw);
  const tokenPhraseNorm = normalizeSearchText(tokens.join(' '));
  const idNorm = normalizeSearchText(article.page_id || article.id || '');
  const descNorm = normalizeSearchText(
    [article.description, article.excerpt].filter(Boolean).join(' '),
  );

  let score = 0;

  // Prefer full typed phrase (keeps stop words: "ten to tunes")
  if (rawPhraseNorm.length >= 2 && titleNorm.includes(rawPhraseNorm)) {
    score += 220;
  } else if (tokenPhraseNorm.length >= 2 && titleNorm.includes(tokenPhraseNorm)) {
    score += 180;
  }
  if (rawPhraseNorm.length >= 2 && titleNorm === rawPhraseNorm) {
    score += 80;
  }

  for (const token of tokens) {
    const t = normalizeSearchText(token);
    if (!t) continue;
    const boundary = new RegExp(`(?:^|\\s)${escapeRegExp(t)}(?:\\s|$)`);

    if (titleNorm === t) score += 70;
    else if (boundary.test(` ${titleNorm} `)) score += 55;
    else if (titleNorm.includes(t)) score += 22;

    if (idNorm && (idNorm === t || idNorm.includes(t))) {
      score += 35;
    }

    if (boundary.test(` ${descNorm} `)) score += 12;
    else if (descNorm.includes(t)) score += 3;
  }

  // Compact page_id hint (tehtotunes ≈ ten + to + tunes)
  const compactId = idNorm.replace(/\s+/g, '');
  if (
    compactId &&
    tokens.length > 1 &&
    tokens.every((tok) => compactId.includes(normalizeSearchText(tok)))
  ) {
    score += 25;
  }

  if (article.featured === 1 || article.featured === true) {
    score += 2;
  }

  return score;
}

export const SITE_SEARCH_SCOPES = [
  { value: 'all', label: 'All' },
  { value: 'products', label: 'Products' },
  { value: 'articles', label: 'Articles' },
  { value: 'tunes', label: 'Tunes & indexes' },
  { value: 'pages', label: 'Site pages' },
];

/** When scope is Tunes (or All), limit tune rows to this page_type grouping. */
export const SITE_SEARCH_TUNE_TYPE_OPTIONS = [
  { value: 'all', label: 'All tune / index types' },
  { value: 'tune-notation', label: 'Tune pages (notation)' },
  { value: 'abc', label: 'ABC notation' },
  { value: 'book-index', label: 'Book index' },
  { value: 'cd-index', label: 'CD index' },
];

/**
 * @param {object} t tune row from API
 * @param {string} filter value from SITE_SEARCH_TUNE_TYPE_OPTIONS
 */
export function tuneMatchesTuneTypeFilter(t, filter) {
  if (!filter || filter === 'all') return true;
  const pt = String(t.page_type || '').toLowerCase().trim();
  if (filter === 'book-index') return pt === 'book-index';
  if (filter === 'cd-index') return pt === 'cd-index';
  if (filter === 'abc') return pt === 'abc-notation' || pt === 'abc-asset';
  if (filter === 'tune-notation') {
    if (pt === 'book-index' || pt === 'cd-index') return false;
    if (pt === 'abc-notation' || pt === 'abc-asset') return false;
    return true;
  }
  return true;
}

export function stripHtmlForSearch(html) {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tuneRowSearchHaystack(t) {
  const metaRaw = t.metadata;
  const metaStr =
    typeof metaRaw === 'string' ? metaRaw : JSON.stringify(metaRaw || {});
  let bookIndexKeys = '';
  let alternateTitles = '';
  try {
    const m = typeof metaRaw === 'string' ? JSON.parse(metaRaw || '{}') : metaRaw || {};
    if (m.book_index_types && typeof m.book_index_types === 'object') {
      bookIndexKeys = Object.keys(m.book_index_types).join(' ');
    }
    alternateTitles = alternateTitlesSearchText(m);
  } catch (_) {
    /* ignore */
  }
  const indexPlain =
    t.index_html && typeof t.index_html === 'string'
      ? stripHtmlForSearch(t.index_html)
      : '';
  return [t.title, t.description, t.page_id, metaStr, bookIndexKeys, alternateTitles, indexPlain]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

export function articleRowSearchHaystack(a) {
  const parts = [a.title, a.description, a.page_id, a.excerpt, a.slug].filter(Boolean);
  if (a.author) parts.push(a.author);
  if (a.created_at) {
    try {
      parts.push(String(new Date(a.created_at).getFullYear()));
    } catch (_) { /* ignore */ }
  }
  if (a.updated_at) {
    try {
      parts.push(String(new Date(a.updated_at).getFullYear()));
    } catch (_) { /* ignore */ }
  }
  if (a.metadata != null) {
    parts.push(
      typeof a.metadata === 'string' ? a.metadata : JSON.stringify(a.metadata)
    );
  }
  if (Array.isArray(a.tags)) {
    parts.push(a.tags.join(' '));
  } else if (typeof a.tags === 'string') {
    parts.push(a.tags);
  }
  return parts.join('\n').toLowerCase();
}

/**
 * For each product ID that appears in a bundle's bundle_products list, collect
 * searchable text from the parent bundle (name, tags, common aliases).
 * Lets "Heritage bundle" match every book in the Heritage Series bundle.
 * @param {object[]} products
 * @returns {Map<number, Set<string>>}
 */
export function buildBundleSearchExpansion(products) {
  /** @type {Map<number, Set<string>>} */
  const byMemberId = new Map();

  for (const bundle of products || []) {
    if (!(bundle.is_bundle === 1 || bundle.is_bundle === true)) continue;
    const raw = bundle.bundle_products;
    if (!raw || !String(raw).trim()) continue;

    const memberIds = String(raw)
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (!memberIds.length) continue;

    const bundleLabelParts = [
      bundle.name,
      bundle.sku,
      bundle.seo_title,
      bundle.seo_description,
      typeof bundle.tags === 'string'
        ? bundle.tags
        : Array.isArray(bundle.tags)
          ? bundle.tags.join(', ')
          : '',
    ].filter(Boolean);

    const bundleText = bundleLabelParts.join(' ').toLowerCase();
    const extraAliases = [];
    if (/heritage/i.test(bundleText)) {
      extraAliases.push(
        'heritage bundle',
        'musical heritage',
        'cape breton musical heritage',
        'heritage series',
      );
    }
    if (/lighthouse/i.test(bundleText)) {
      extraAliases.push(
        'lighthouse bundle',
        'lighthouse collection bundle',
      );
    }

    for (const mid of memberIds) {
      if (!byMemberId.has(mid)) byMemberId.set(mid, new Set());
      const set = byMemberId.get(mid);
      for (const part of bundleLabelParts) set.add(part);
      for (const alias of extraAliases) set.add(alias);
    }
  }

  return byMemberId;
}

/**
 * @param {object} p product row
 * @param {Map<number, Set<string>>} [bundleExpansionByMemberId] from buildBundleSearchExpansion
 */
export function productRowSearchHaystack(p, bundleExpansionByMemberId) {
  const parts = [
    p.name,
    p.description,
    p.author,
    p.artist,
    p.sku,
    p.permalink,
    p.id != null ? String(p.id) : '',
    p.category,
    p.type,
    typeof p.tags === 'string' ? p.tags : Array.isArray(p.tags) ? p.tags.join(' ') : '',
    p.seo_title,
    p.seo_description,
    p.social_media_title,
    p.social_media_description,
    Array.isArray(p.categories) ? p.categories.map((c) => (typeof c === 'string' ? c : c?.name)).filter(Boolean).join(' ') : '',
  ];

  // Normalized title/author words for partial title search (Stubbert's → stubbert, etc.)
  for (const label of [p.name, p.author, p.artist, p.seo_title]) {
    if (!label) continue;
    parts.push(normalizeSearchText(label));
    parts.push(titleSearchWords(label).join(' '));
  }

  if (bundleExpansionByMemberId && p.id != null) {
    const extra = bundleExpansionByMemberId.get(Number(p.id));
    if (extra?.size) parts.push([...extra].join(' '));
  }

  return parts.filter(Boolean).join('\n').toLowerCase();
}

export function tuneResultDisplayTag(t) {
  if (t.page_type === 'book-index') return '[Book index]';
  if (t.page_type === 'cd-index') return '[CD index]';
  if (t.page_type === 'abc-notation') return '[ABC]';
  return '[Tune]';
}

/**
 * @param {object} opts
 * @param {string} opts.searchText
 * @param {SiteSearchScope} opts.scope
 * @param {object[]} opts.products
 * @param {object[]} opts.articles
 * @param {object[]} opts.tunes
 * @param {{title:string,route:string,content:string}[]} opts.staticPages
 * @param {string} [opts.tuneTypeFilter] SITE_SEARCH_TUNE_TYPE_OPTIONS value; applies to tune hits when scope is all or tunes
 */
export function runSiteSearch({
  searchText,
  scope = 'all',
  products = [],
  articles = [],
  tunes = [],
  staticPages = [],
  tuneTypeFilter = 'all',
}) {
  const tokens = tokenizeQuery(searchText);
  if (!tokens.length) return [];

  const want = (cat) => scope === 'all' || scope === cat;
  const tuneFilterActive =
    tuneTypeFilter &&
    tuneTypeFilter !== 'all' &&
    (scope === 'all' || scope === 'tunes');
  const out = [];
  const bundleExpansion = buildBundleSearchExpansion(products);

  if (want('products')) {
    for (const p of products) {
      const haystack = productRowSearchHaystack(p, bundleExpansion);
      if (!queryMatchesSearchText(haystack, searchText, { title: p.name })) continue;
      const usePermalink = p.permalink && String(p.permalink).trim() !== '';
      const id = usePermalink ? p.permalink : p.id;
      const descParts = [p.author || p.artist, p.description].filter(Boolean);
      out.push({
        type: 'product',
        id,
        name: p.name,
        image: p.image,
        description: descParts[0] || '',
        route: `/product/${id}`,
        price_cents: p.price_cents ?? null,
        author: p.author || p.artist || null,
        category: p.category || null,
      });
    }
  }

  if (want('articles')) {
    const articleHits = [];
    for (const a of articles) {
      if (!queryMatchesSearchText(articleRowSearchHaystack(a), searchText, { title: a.title })) {
        continue;
      }
      articleHits.push({
        type: 'article',
        id: a.page_id,
        title: a.title || a.page_id,
        description: a.description || a.excerpt || '',
        route: `/page/${a.page_id}`,
        score: scoreArticleSearchRelevance(a, searchText),
      });
    }
    articleHits.sort((x, y) => (y.score || 0) - (x.score || 0));
    out.push(...articleHits);
  }

  if (want('tunes')) {
    for (const t of tunes) {
      if (tuneFilterActive && !tuneMatchesTuneTypeFilter(t, tuneTypeFilter)) continue;
      if (!matchesAllTokens(tuneRowSearchHaystack(t), tokens)) continue;
      // Parse stored metadata JSON for rich display fields
      let meta = {};
      try { meta = JSON.parse(t.metadata || '{}'); } catch (_) {}
      out.push({
        type: 'tune',
        id: t.page_id,
        title: t.title || t.page_id,
        description: t.description || '',
        route: `/page/${t.page_id}`,
        tuneTag: tuneResultDisplayTag(t),
        pageType: t.page_type,
        tuneType:      meta.tune_type || '',
        keySignature:  meta.key_signature || meta.key || '',
        collectionBook: meta.collection_book || '',
        recordingAlbum: meta.recording_album || meta.album || '',
        composer:      meta.composer || '',
        region:        meta.region || '',
      });
    }
  }

  if (want('pages')) {
    for (const p of staticPages) {
      const hay = `${p.title}\n${p.content}`.toLowerCase();
      if (!matchesAllTokens(hay, tokens)) continue;
      out.push({
        type: 'page',
        title: p.title,
        description: p.content ? p.content.slice(0, 160) : '',
        route: p.route,
      });
    }
  }

  return out;
}
